import type { KnowledgeBaseCategoryStatusDto, KnowledgeBaseSummaryViewDto } from '@/types/knowledgeBase'
import { inWords } from '@/components/console/knowledgeBase/brandGuidelinesModel'
import { isDraft } from '@/components/console/knowledgeBase/knowledgeBaseModel'
import { DraftMark, EmptyMark, ReviewedMark, WarningMark } from './entryStatus'

interface Props {
  summary: KnowledgeBaseSummaryViewDto
  entries: readonly KnowledgeBaseCategoryStatusDto[]
  /** Background and Text, whichever the brand lacks, once its entry has loaded; null until then. */
  missingColorRoles: readonly string[] | null
  /** Absent when the person cannot add the address at this level. */
  onAddAddress?: () => void
  /** Absent when the person cannot change the colors from here. */
  colorsHref?: string
}

const ACTION = 'font-medium text-primary-700 dark:text-primary-400 underline underline-offset-[3px] hover:text-slate-900 dark:hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-sm'

/**
 * The knowledge base's readiness in one band (ORB-904): reviewed, Webster's drafts, empty, then whether emails can send.
 * ⚑ Every count and the email line come from the summary as the server sent it. The email line is the rule the email
 * refusals use, so it and a refused email cannot disagree.
 */
export function KnowledgeBaseReadiness({ summary, entries, missingColorRoles, onAddAddress, colorsHref }: Props) {
  const drafts = entries.filter(isDraft).length
  const empty = entries.filter(status => !status.IsPopulated).length
  const reviewed = entries.length - drafts - empty
  const { PostalAddressMissing: noAddress, EmailColorsMissing: noColors } = summary
  const colors = missingColorRoles && missingColorRoles.length > 0 ? missingColorRoles : ['Background', 'Text']
  const missing = [
    noAddress ? 'the postal address' : null,
    noColors ? `the ${inWords(colors)} ${colors.length === 1 ? 'color' : 'colors'}` : null,
  ].filter((part): part is string => part !== null)
  const singular = missing.length === 1 && (!noColors || colors.length === 1)

  return (
    <ul aria-label="Readiness" className="px-6 py-3.5 flex flex-wrap items-center gap-x-7 gap-y-2.5 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300">
      <li className="flex items-center gap-2">
        <ReviewedMark className="w-4 h-4 flex-none" />
        <span><span className="font-medium text-slate-900 dark:text-white">{reviewed}</span> of {entries.length} reviewed</span>
      </li>
      <li className="flex items-center gap-2">
        <DraftMark className="w-4 h-4 flex-none" />
        <span><span className="font-medium text-slate-900 dark:text-white">{drafts}</span> Webster {drafts === 1 ? 'draft' : 'drafts'} to review</span>
      </li>
      <li className="flex items-center gap-2">
        <EmptyMark className="w-4 h-4 flex-none text-slate-400 dark:text-slate-500" />
        <span><span className="font-medium text-slate-900 dark:text-white">{empty}</span> empty</span>
      </li>
      <li className="basis-full">
        {missing.length === 0 ? (
          <span className="flex items-center gap-2">
            <ReviewedMark className="w-4 h-4 flex-none" />
            Emails can send: the postal address and the Background and Text colors are set
          </span>
        ) : (
          <span className="flex flex-wrap items-center gap-x-2 text-amber-800 dark:text-amber-300">
            <WarningMark className="w-4 h-4 flex-none" />
            <span className="font-medium">Emails can&#8217;t send: {inWords(missing)} {singular ? 'is' : 'are'} missing</span>
            {noAddress && onAddAddress && <button type="button" className={`ml-1 ${ACTION}`} onClick={onAddAddress}>Add the address</button>}
            {noColors && colorsHref && <a href={colorsHref} className={`ml-1 ${ACTION}`}>Add the colors</a>}
          </span>
        )}
      </li>
    </ul>
  )
}
